import type { IframePoolOrderItem } from '../../modules/api';

export interface MyPacksStatsRowProps {
  inventory: IframePoolOrderItem[];
  packsOpened: number;
}

/**
 * Three stats: packs opened, sum of fair-market values, sum of buyback
 * offers. Two responsive layouts:
 *   • Mobile (Figma 278:7388) — three full-width glass-frosted pills
 *     stacked vertically, label on the left, value on the right.
 *   • ≥600px — inline row separated by 3px dots (the old layout).
 */
export function MyPacksStatsRow({ inventory, packsOpened }: MyPacksStatsRowProps) {
  const totalInsured = sumBy(inventory, (i) => i.fairMarketValue ?? 0);
  const totalBuyback = sumBy(inventory, (i) => i.bestActiveOffer?.grossPrice ?? 0);

  const stats: { label: string; value: string }[] = [
    { label: 'Packs Opened', value: String(packsOpened) },
    { label: 'Total Insured Value', value: formatUsd(totalInsured) },
    { label: 'Total Buyback Value', value: formatUsd(totalBuyback) },
  ];

  return (
    <div
      data-co-component="MyPacksStatsRow"
      className="
        flex w-full flex-col gap-2
        @[600px]:flex-row @[600px]:flex-wrap @[600px]:items-center @[600px]:gap-[14px]
      "
      style={{
        fontFamily: 'var(--co-MyPacksStatsRow-fontFamily), Inter, system-ui, sans-serif',
        color: 'var(--co-MyPacksStatsRow-valueColor, #FFFFFF)',
        fontSize: 'var(--co-MyPacksStatsRow-valueSize, 13px)',
        lineHeight: '20px',
      }}
    >
      {stats.map((s, i) => (
        <span key={s.label} className="contents">
          {i > 0 && (
            <span
              aria-hidden
              className="hidden size-[3px] shrink-0 rounded-full @[600px]:inline-block"
              style={{
                background:
                  'var(--co-MyPacksStatsRow-separatorColor, rgba(255,255,255,0.40))',
              }}
            />
          )}
          <Stat label={s.label} value={s.value} />
        </span>
      ))}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  // Mobile (< 600px container): glass-frosted pill (Figma 278:7388) —
  // tinted background + blur. Desktop (≥ 600px): inline text pair, no
  // fill/blur. Mobile-only paint lives behind `@max-[600px]:` rather
  // than as the base + a `@[600px]:` override, because some of the
  // override utilities (`backdrop-blur-0`, arbitrary `[bg-image:none]`)
  // don't reliably outweigh their base counterparts in Tailwind 4 and
  // would leave the blur/tint visible on desktop.
  return (
    <div
      className="
        flex w-full items-center justify-between gap-3
        @max-[600px]:rounded-[12px] @max-[600px]:px-4 @max-[600px]:py-2.5
        @max-[600px]:[background-image:linear-gradient(180deg,rgba(255,255,255,0.20)_0%,rgba(153,153,153,0.20)_100%),linear-gradient(90deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.18)_100%)]
        @max-[600px]:backdrop-blur-[27px]
        @[600px]:w-auto @[600px]:gap-[5px] @[600px]:justify-start
        @[600px]:whitespace-nowrap
      "
    >
      <span>
        <span className="@[600px]:hidden">{label}</span>
        <span className="hidden @[600px]:inline">{label}:</span>
      </span>
      <span>{value}</span>
    </div>
  );
}

function sumBy<T>(items: T[], pick: (i: T) => number): number {
  return items.reduce((acc, item) => acc + (pick(item) || 0), 0);
}

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}
